import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Input, Empty } from 'antd';
import { SearchOutlined, CheckOutlined, MinusOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import _ from 'lodash';
import {
  FieldLabel,
  SwitchRow,
  ModeLink,
  QuickPickRow,
  QuickPickTag,
  PickerBox,
  GroupRow,
  GroupMeta,
  CollapseButton,
  PartnerRow,
  PartnerVersion,
  PickerFooter,
  LinkButton,
} from './style';

const NO_GROUP = 'ไม่ระบุกลุ่ม'
const BY_STATEMENT_DATE = 'statement_date'
// ยังไม่เลือก lender ก็ไม่ต้องกันที่เต็มความสูง
const EMPTY_HEIGHT = 110

// Custom control ของ Form.Item name="partner_ids" — รับ/ส่งเป็น array ของ partner id ล้วน
const PartnerPicker = ({ partners, value, onChange, loading, height }) => {
  const [keyword, setKeyword] = React.useState('')
  const [groupBy, setGroupBy] = React.useState(BY_STATEMENT_DATE)
  const [collapsed, setCollapsed] = React.useState([])

  const toggleCollapse = (key) => {
    setCollapsed((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])
  }

  // ระหว่างค้นหาต้องเห็นผลลัพธ์เสมอ ไม่งั้นพิมพ์แล้วเจอกลุ่มที่พับไว้ จะดูเหมือนหาไม่เจอ
  const isCollapsed = (key) => !keyword && collapsed.includes(key)

  const selectedIds = value || []
  const selected = React.useMemo(() => new Set(selectedIds), [selectedIds])

  // เปลี่ยน lender แล้วรายชื่อชุดใหม่ ล้างคำค้นเดิมทิ้ง ไม่งั้นลิสต์อาจว่างโดยไม่รู้สาเหตุ
  React.useEffect(() => { setKeyword('') }, [partners])

  // สลับวิธีจัดกลุ่มแล้ว key ของกลุ่มคนละชุดกัน สถานะพับที่ค้างไว้ใช้ต่อไม่ได้
  React.useEffect(() => { setCollapsed([]) }, [partners, groupBy])

  // ปุ่มเลือกด่วน: วันตัดรอบทั้งหมดของ lender ที่เลือก พร้อมจำนวนบริษัท (ไม่ผูกกับคำค้นหา)
  const statementDates = React.useMemo(() => {
    const grouped = _.groupBy(partners.filter(({ statement_date }) => statement_date), 'statement_date')

    return Object.keys(grouped)
      .sort((a, b) => a - b)
      .map((date) => ({ date, ids: _.map(grouped[date], 'id') }))
  }, [partners])

  // ค้นได้ทั้งชื่อบริษัทและชื่อกลุ่ม — พิมพ์ชื่อกลุ่มแล้วต้องเห็นทุกบริษัทในกลุ่มนั้น
  const visiblePartners = React.useMemo(() => {
    const kw = keyword.trim().toLowerCase()

    if (!kw) return partners

    return partners.filter(({ name, partner_group }) => (
      `${name}`.toLowerCase().includes(kw) || `${partner_group || ''}`.toLowerCase().includes(kw)
    ))
  }, [partners, keyword])

  const groups = React.useMemo(() => {
    const byStatementDate = groupBy === BY_STATEMENT_DATE
    const grouped = _.groupBy(visiblePartners, (partner) => (
      byStatementDate ? (partner.statement_date || '-') : (partner.partner_group || NO_GROUP)
    ))

    return Object.keys(grouped)
      .sort(byStatementDate ? (a, b) => a - b : (a, b) => a.localeCompare(b))
      .map((key) => ({
        key,
        label: byStatementDate ? `วันตัดรอบ ${key}` : key,
        items: _.sortBy(grouped[key], 'name'),
      }))
  }, [visiblePartners, groupBy])

  const toggleMany = (ids, checked) => {
    onChange(checked ? _.union(selectedIds, ids) : _.difference(selectedIds, ids))
  }

  const countSelected = (ids) => ids.filter((id) => selected.has(id)).length

  if (partners.length === 0) {
    return (
      <PickerBox $height={EMPTY_HEIGHT} $empty>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={loading ? 'กำลังโหลดพาร์ทเนอร์...' : 'เลือก Lender ก่อน'}
        />
      </PickerBox>
    )
  }

  return (
    <>
      {statementDates.length > 0 &&
        <>
          <FieldLabel>เลือกด่วนตามวันตัดรอบ</FieldLabel>
          <QuickPickRow>
            {statementDates.map(({ date, ids }) => {
              const picked = countSelected(ids)
              const all = picked === ids.length
              const partial = picked > 0 && !all

              return (
                <QuickPickTag
                  key={`quick-${date}`}
                  $active={all}
                  $partial={partial}
                  onClick={() => toggleMany(ids, !all)}
                >
                  {all && <CheckOutlined />}
                  {partial && <MinusOutlined />}
                  {`วันที่ ${date} · ${ids.length} บริษัท`}
                </QuickPickTag>
              )
            })}
          </QuickPickRow>
        </>
      }

      <QuickPickRow>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="ค้นหาชื่อบริษัท หรือ กลุ่มบริษัท"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </QuickPickRow>

      <SwitchRow>
        <span>จัดกลุ่ม</span>
        {[
          { value: BY_STATEMENT_DATE, label: 'วันตัดรอบ' },
          { value: 'partner_group', label: 'กลุ่มบริษัท' },
        ].map(({ value: mode, label }) => (
          <ModeLink key={`mode-${mode}`} $active={groupBy === mode} onClick={() => setGroupBy(mode)}>
            {label}
          </ModeLink>
        ))}
      </SwitchRow>

      <PickerBox $height={height}>
        {groups.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="ไม่พบบริษัทที่ค้นหา" />}
        {groups.map(({ key, label, items }) => {
          const ids = _.map(items, 'id')
          const picked = countSelected(ids)
          const folded = isCollapsed(key)

          return (
            <React.Fragment key={`group-${key}`}>
              <GroupRow>
                <Checkbox
                  checked={picked === ids.length}
                  indeterminate={picked > 0 && picked < ids.length}
                  onChange={(e) => toggleMany(ids, e.target.checked)}
                >
                  {label}
                </Checkbox>
                <GroupMeta>
                  <span>{`${picked}/${ids.length}`}</span>
                  <CollapseButton
                    aria-label={folded ? `เปิด ${label}` : `พับ ${label}`}
                    onClick={() => toggleCollapse(key)}
                  >
                    {folded ? <RightOutlined /> : <DownOutlined />}
                  </CollapseButton>
                </GroupMeta>
              </GroupRow>
              {!folded && items.map(({ id, name, version }) => (
                <PartnerRow key={`partner-${id}`}>
                  <Checkbox
                    checked={selected.has(id)}
                    onChange={(e) => toggleMany([id], e.target.checked)}
                  >
                    {name}
                  </Checkbox>
                  <PartnerVersion>{`v${version}`}</PartnerVersion>
                </PartnerRow>
              ))}
            </React.Fragment>
          )
        })}
      </PickerBox>

      <PickerFooter>
        <span>
          {'เลือกแล้ว '}<strong>{selectedIds.length}</strong>{` / ${partners.length} บริษัท`}
        </span>
        <span>
          <LinkButton onClick={() => toggleMany(_.map(visiblePartners, 'id'), true)}>
            {keyword ? 'เลือกทั้งหมดที่ค้นเจอ' : 'เลือกทั้งหมด'}
          </LinkButton>
          <LinkButton onClick={() => onChange([])} disabled={selectedIds.length === 0}>ล้างทั้งหมด</LinkButton>
        </span>
      </PickerFooter>
    </>
  );
}

PartnerPicker.propTypes = {
  partners: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    version: PropTypes.number,
    partner_group: PropTypes.string,
    statement_date: PropTypes.number,
  })).isRequired,
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  height: PropTypes.number,
}

export default PartnerPicker
